import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../../../serialization/enum-utils.js';
import { MapUtils } from '../../../../utility/map-utils.js';
import { VersionRange } from '../../../core/version-range.js';
import {
  cloneData, cloneDataOrNull, cloneListOfCloneable, DataModel, DataModelBuilder,
  isSameData, isSameList, SerializationContext, SerializedData,
} from '../../../dto/dto.js';
import { GenericOrderEntry, GenericOrderEntryStatus } from '../generic-order-entry.js';
import { OnlineTransactionDetails } from '../../online-transaction-details.js';
import { BusinessModuleRef } from '../../business-module-ref.js';
import { Price } from '../../price.js';
import { InvoiceVat } from '../../invoicing/invoice-vat.js';
import { TimeZone } from '../../../../time/timezone.js';
import { Dayjs } from 'dayjs';
import { ElectronicDeliveryMethod } from '../../electronic-delivery-method.js';
import { Refund } from '../../discounts/refund.js';
import { OfflineTransactionDetails } from '../../offline-transaction-details.js';
import { GiftCard, GiftCardStatus } from '../../gift-card/gift-card.js';

export class GenericOrderGiftCardEntry extends GenericOrderEntry {
  static readonly TYPE_ID: string = '9c84a834ddf546c3817aea7068f72c9b';
  static readonly TYPE_NAME: string = 'GiftCard';
  static readonly SCHEMA_VERSION: number = 1;

  private constructor(
    public readonly uniqueId: string,
    public readonly orderId: string,
    public readonly entryGroupId: string | null,
    public readonly note: string | null,
    public readonly status: GenericOrderEntryStatus,
    public readonly creatorId: string,
    public readonly creationDate: Dayjs,
    public readonly onlineTransactionDetails: OnlineTransactionDetails | null,
    public readonly offlineTransactionDetails: OfflineTransactionDetails | null,
    public readonly cancelledDate: Dayjs | null,
    public readonly cancelledBy: string | null,
    public readonly businessRef: BusinessModuleRef,
    public readonly giftCards: GiftCard[],
    public readonly deliveryMethod: ElectronicDeliveryMethod,
    public readonly refunds: Refund[],
    public applicationFee: Price | null,
    public applicationFeeVat: InvoiceVat | null,
  ) {
    super();
  }

  get $typeId(): string {
    return GenericOrderGiftCardEntry.TYPE_ID;
  }

  get $typeName(): string {
    return GenericOrderGiftCardEntry.TYPE_NAME;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(GenericOrderGiftCardEntry.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    orderId: string,
    entryGroupId: string | null,
    note: string | null,
    status: GenericOrderEntryStatus,
    creatorId: string,
    creationDate: Dayjs,
    onlineTransactionDetails: OnlineTransactionDetails | null,
    offlineTransactionDetails: OfflineTransactionDetails | null,
    cancelledDate: Dayjs | null,
    cancelledBy: string | null,
    businessRef: BusinessModuleRef,
    giftCards: GiftCard[],
    deliveryMethod: ElectronicDeliveryMethod,
    applicationFee: Price | null,
    applicationFeeVat: InvoiceVat | null,
    refunds?: Refund[],
  }): GenericOrderGiftCardEntry {
    return new GenericOrderGiftCardEntry(
        args.uniqueId,
        args.orderId,
        args.entryGroupId,
        args.note,
        args.status,
        args.creatorId,
        args.creationDate,
        args.onlineTransactionDetails,
        args.offlineTransactionDetails,
        args.cancelledDate,
        args.cancelledBy,
        args.businessRef,
        args.giftCards,
        args.deliveryMethod,
        args.refunds ?? [],
        args.applicationFee,
        args.applicationFeeVat,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GenericOrderGiftCardEntry {
    return GenericOrderGiftCardEntry.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      orderId: MapUtils.getString(map, 'orderId'),
      entryGroupId: MapUtils.getStringOrNull(map, 'entryGroupId'),
      note: MapUtils.getStringOrNull(map, 'note'),
      status: toEnumFromString(MapUtils.getString(map, '$status'), GenericOrderEntryStatus),
      creatorId: MapUtils.getString(map, 'creatorId'),
      creationDate: context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
      onlineTransactionDetails: context.fromDataOrNull<OnlineTransactionDetails>(MapUtils.getRecordOrNull(map, 'onlineTransactionDetails')),
      offlineTransactionDetails: context.fromDataOrNull<OfflineTransactionDetails>(MapUtils.getRecordOrNull(map, 'offlineTransactionDetails')),
      cancelledDate: context.decodeDateOrNull(MapUtils.getIntegerOrNull(map, 'cancelledDate')),
      cancelledBy: MapUtils.getStringOrNull(map, 'cancelledBy'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      giftCards: MapUtils.getList<GiftCard>(map, 'giftCards', (value) => {
        return context.fromData<GiftCard>(value as SerializedData);
      }),
      applicationFee: Price.decodeOrNull(MapUtils.getStringOrNull(map, 'applicationFee')),
      applicationFeeVat: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'applicationFeeVat'), InvoiceVat),
      deliveryMethod: toEnumFromString(MapUtils.getString(map, 'deliveryMethod'), ElectronicDeliveryMethod),
      refunds: MapUtils.getList<Refund>(map, 'refunds', (value) => {
        return context.fromData<Refund>(value as SerializedData);
      }),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'orderId': this.orderId,
      'entryGroupId': this.entryGroupId,
      'note': this.note,
      '$status': fromEnumAsString(this.status),
      'creatorId': this.creatorId,
      'creationDate': context.encodeDate(this.creationDate),
      'onlineTransactionDetails': context.toDataOrNull(this.onlineTransactionDetails),
      'offlineTransactionDetails': context.toDataOrNull(this.offlineTransactionDetails),
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
      'businessRef': this.businessRef.encode(),
      'giftCards': this.giftCards.map((e) => context.toData(e)),
      'applicationFee': this.applicationFee?.encode() ?? null,
      'applicationFeeVat': fromEnumAsStringOrNull(this.applicationFeeVat),
      'deliveryMethod': fromEnumAsString(this.deliveryMethod),
      'refunds': this.refunds.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return this.copyWith() as this;
  }

  copyWith({
    uniqueId = this.uniqueId,
    orderId = this.orderId,
    entryGroupId = this.entryGroupId,
    note = this.note,
    status = this.status,
    creatorId = this.creatorId,
    creationDate = TimeZone.cloneDate(this.creationDate),
    onlineTransactionDetails = cloneDataOrNull(this.onlineTransactionDetails),
    offlineTransactionDetails = cloneDataOrNull(this.offlineTransactionDetails),
    cancelledDate = TimeZone.cloneDateOrNull(this.cancelledDate),
    cancelledBy = this.cancelledBy,
    businessRef = cloneData(this.businessRef),
    giftCards = cloneListOfCloneable(this.giftCards),
    applicationFee = cloneDataOrNull(this.applicationFee),
    applicationFeeVat = this.applicationFeeVat,
    deliveryMethod = this.deliveryMethod,
    refunds = cloneListOfCloneable(this.refunds),
  }: {
    uniqueId?: string,
    orderId?: string,
    entryGroupId?: string | null,
    note?: string | null,
    status?: GenericOrderEntryStatus,
    creatorId?: string,
    creationDate?: Dayjs,
    onlineTransactionDetails?: OnlineTransactionDetails | null,
    offlineTransactionDetails?: OfflineTransactionDetails | null,
    cancelledDate?: Dayjs | null,
    cancelledBy?: string | null,
    businessRef?: BusinessModuleRef,
    giftCards?: GiftCard[],
    applicationFee?: Price | null,
    applicationFeeVat?: InvoiceVat | null,
    deliveryMethod?: ElectronicDeliveryMethod,
    refunds?: Refund[],
  } = {}): this {
    return GenericOrderGiftCardEntry.create({
      uniqueId,
      orderId,
      entryGroupId,
      note,
      status,
      creatorId,
      creationDate,
      onlineTransactionDetails,
      offlineTransactionDetails,
      cancelledDate,
      cancelledBy,
      businessRef,
      giftCards,
      applicationFee,
      applicationFeeVat,
      deliveryMethod,
      refunds,
    }) as this;
  }

  copyAsAborted(): this {
    return this.copyWith({
      status: GenericOrderEntryStatus.Aborted,
      giftCards: this.giftCards.map((e) => e.copyAsAborted()),
    });
  }

  copyAsCanceled(cancelledBy: string | null, cancelledDate: Dayjs, isRefund: boolean): this {
    return this.copyWith({
      status: isRefund ? GenericOrderEntryStatus.Refunded : GenericOrderEntryStatus.Canceled,
      cancelledDate: TimeZone.cloneDateOrNull(this.cancelledDate) ?? cancelledDate,
      cancelledBy: this.cancelledBy ?? cancelledBy,
      giftCards: this.giftCards.map((giftCard) => {
        const shouldRefund = isRefund || giftCard.status === GiftCardStatus.Refunded;
        return giftCard.copyAsCanceled(cancelledBy, cancelledDate, shouldRefund);
      }),
    });
  }

  copyWithOnlineTransactionDetails(transactionDetails: OnlineTransactionDetails): this {
    return this.copyWith({
      onlineTransactionDetails: transactionDetails,
    });
  }

  copyWithOfflineTransactionDetails(transactionDetails: OfflineTransactionDetails): this {
    return this.copyWith({
      offlineTransactionDetails: transactionDetails,
    });
  }

  copyWithTicketCloner(cloner: (t: GiftCard) => GiftCard): this {
    return this.copyWith({ giftCards: this.giftCards.map(cloner) });
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.orderId === this.orderId &&
      other.entryGroupId === this.entryGroupId &&
      other.note === this.note &&
      other.status === this.status &&
      other.creatorId === this.creatorId &&
      TimeZone.isSameInstant(other.creationDate, this.creationDate) &&
      isSameData(other.onlineTransactionDetails, this.onlineTransactionDetails) &&
      isSameData(other.offlineTransactionDetails, this.offlineTransactionDetails) &&
      TimeZone.isSameInstantOrNull(other.cancelledDate, this.cancelledDate) &&
      other.cancelledBy === this.cancelledBy &&
      isSameData(other.businessRef, this.businessRef) &&
      isSameList(other.giftCards, this.giftCards, isSameData) &&
      isSameData(other.applicationFee, this.applicationFee) &&
      other.applicationFeeVat === this.applicationFeeVat &&
      other.deliveryMethod === this.deliveryMethod &&
      isSameList(other.refunds, this.refunds, isSameData);
  }

  tryGetApplicationFee(): Price | null {
    return this.applicationFee;
  }

  tryGetApplicationFeeVat(): InvoiceVat | null {
    return this.applicationFeeVat;
  }

  tryGetRefunds(): Refund[] | null {
    return this.refunds;
  }

  tryAddRefund(refund: Refund): boolean {
    this.refunds.push(refund);
    return true;
  }

  tryGetEventId(): string | null {
    return null;
  }

  static verifyType(entry: DataModel): boolean {
    return entry instanceof GenericOrderGiftCardEntry;
  }

  static assertType(entry: DataModel): entry is GenericOrderGiftCardEntry {
    return entry instanceof GenericOrderGiftCardEntry;
  }
}

export class GenericOrderGiftCardEntryBuilder extends DataModelBuilder<GenericOrderGiftCardEntry> {
  build(context: SerializationContext, map: SerializedData): GenericOrderGiftCardEntry {
    return GenericOrderGiftCardEntry.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, '$status', GenericOrderEntryStatus, GenericOrderEntryStatus.Open);

    if(versionRange.contains(GenericOrderGiftCardEntry.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(GenericOrderGiftCardEntry.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    const version = versionRange.to;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
